import { assert, ChangeEventListener, copyObjectWithoutKeys, delay, Droppable, EagerThrottler, EventListener } from '../../../utils'

import { type Capability, ConnectwareError, ConnectwareErrorType } from '../../../domain'

import type {
    AllSubscriptionsTypes,
    CountSubscriptionsTypes,
    PageSubscription,
    PageSubscriptionsTypes,
    SingleSubscriptionsTypes,
    SubscriptionEventHandler,
    SubscriptionFilterArgs,
    SubscriptionPageEvent,
    SubscriptionPageOptions,
    SubscriptionsService,
    TranslationService,
} from '../../../application'

import type { RSTAdapter } from '../../Connectware'
import { type ConnectwareHTTPServiceOptions, FetchConnectwareHTTPService } from '../Base'
import { Fetcher } from './Fetcher'
import { PageSubscriptionAdapter } from './PageSubscriptionAdapter'
import { getSubscriptionStrategyFactory, type SubscriptionStrategy } from './strategies'

type SubscriptionsServiceOptions = Readonly<{
    getCapabilities: () => readonly Capability[]
    translationService: TranslationService
    resourceChangeListeners: Pick<EventListener<void>, 'on'>
    rstAdapter: RSTAdapter

    pollingInterval: number
    updateThrottle: number
}>

/**
 * The http implementation for subscribing to changes of resources on the Connectware
 */
export class ConnectwareHTTPSubscriptionsService extends FetchConnectwareHTTPService implements SubscriptionsService {
    private readonly pageSubscriptionAdapter: PageSubscriptionAdapter

    private readonly getCapabilities: SubscriptionsServiceOptions['getCapabilities']
    private readonly resourceChangeListeners: SubscriptionsServiceOptions['resourceChangeListeners']
    private readonly rstAdapter: SubscriptionsServiceOptions['rstAdapter']
    private readonly pollingInterval: SubscriptionsServiceOptions['pollingInterval']
    private readonly updateThrottle: SubscriptionsServiceOptions['updateThrottle']

    private static validateFilters<P extends keyof PageSubscriptionsTypes, S extends keyof SingleSubscriptionsTypes> (
        strategy: SubscriptionStrategy<P, S>,
        filter: SubscriptionFilterArgs
    ): void {
        assert(
            strategy.supportsFilters(filter),
            new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'This strategy does NOT support this filter', { filter })
        )
    }

    constructor ({
        resourceChangeListeners,
        getCapabilities,
        translationService,
        rstAdapter,
        pollingInterval,
        updateThrottle,
        ...options
    }: SubscriptionsServiceOptions & ConnectwareHTTPServiceOptions) {
        super(options)

        this.getCapabilities = getCapabilities
        this.resourceChangeListeners = resourceChangeListeners
        this.rstAdapter = rstAdapter
        this.pollingInterval = pollingInterval
        this.updateThrottle = updateThrottle

        this.pageSubscriptionAdapter = new PageSubscriptionAdapter((eventName, filter, listener) => {
            const droppable = new Droppable()

            const strategy = this.createStrategy(eventName)

            const fetcher = new Fetcher(
                () => {
                    ConnectwareHTTPSubscriptionsService.validateFilters(strategy, filter)
                    return strategy.retrieveAll(filter)
                },
                (...args) => strategy.compareAll(...args)
            )

            droppable.onDrop(fetcher.onChange(listener))
            droppable.onDrop(this.createFetcherUnsubber((handler) => strategy.createAllChangeListener?.(filter, handler), fetcher))

            this.scheduleFetcherStart(fetcher)

            return Promise.resolve(() => droppable.drop())
        }, translationService)
    }

    private createStrategy<P extends keyof PageSubscriptionsTypes, S extends keyof SingleSubscriptionsTypes> (eventName: P | S): SubscriptionStrategy<P, S> {
        const Strategy = getSubscriptionStrategyFactory<P, S>(eventName)
        const strategy = new Strategy((args) => this.request(args), this.rstAdapter)

        const userPermissions = new Set(this.getCapabilities())
        const { requiredPermissions } = strategy

        assert(
            requiredPermissions.every((p) => userPermissions.has(p)),
            new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, "Can't subscribe to this type due to permissioning issues", {
                userPermissions: Array.from(userPermissions),
                requiredPermissions,
                eventName,
            })
        )

        return strategy
    }

    private createFetcherUnsubber<F> (createChangeListener: (handler: VoidFunction) => Promise<VoidFunction> | void, fetcher: Fetcher<F>): VoidFunction {
        const droppable = new Droppable()
        const throttler = new EagerThrottler(this.updateThrottle)

        const update: VoidFunction = () => fetcher.update()

        /**
         * Set up listener for any changes made by the current user
         * Since this was done by the user, simply update things straight away
         */
        droppable.onDrop(this.resourceChangeListeners.on(update))

        /**
         * Set up listener for any changes detected by the strategy
         * This can potentially be spammy (in case a lot of changes are detected)
         *
         * Thus, an initial attempt to update is made
         * If it's blocked, another one after the current timeout is also done
         *
         * If both get denied, it means there are other calls being made,
         * which can simply be waited on
         */
        const unsubToChanges = createChangeListener(() => {
            const executed = throttler.run(update)
            if (!executed) {
                void throttler.runAfter(update)
            }
        })

        if (unsubToChanges) {
            void unsubToChanges.then((unsub) => droppable.onDrop(unsub))
        }

        droppable.onDrop(() => fetcher.stop())

        return () => droppable.drop()
    }

    private scheduleFetcherStart<F> (fetcher: Fetcher<F>): void {
        /**
         * Start fetcher after object has been returned
         */
        void delay(0).then(() => fetcher.start(this.pollingInterval))
    }

    subscribeToPage<P extends keyof PageSubscriptionsTypes> (
        eventName: P,
        initialOptions: SubscriptionFilterArgs & SubscriptionPageOptions<P>
    ): Promise<PageSubscription<P>> {
        const strategy = this.createStrategy(eventName)

        if (!strategy.supportsPagination) {
            /**
             * Since not supported, use the fake pagination
             */
            return this.pageSubscriptionAdapter.subscribeToPage(eventName, initialOptions)
        }

        const droppable = new Droppable()
        const listener = new EventListener<SubscriptionPageEvent<P>>()
        const optionsListener = new ChangeEventListener(initialOptions, true)

        const fetcher = new Fetcher(
            () => {
                ConnectwareHTTPSubscriptionsService.validateFilters(
                    strategy,
                    /**
                     * Why is an object being copied here?
                     * The idea behind this is: if the object is extended, errors will be detected
                     */
                    copyObjectWithoutKeys<typeof optionsListener.value, keyof SubscriptionPageOptions<P>>(optionsListener.value, 'search', 'sort', 'pagination')
                )
                return strategy.retrievePage(optionsListener.value)
            },
            (...args) => strategy.comparePage(...args)
        )

        /**
         * Whenever there is a change in filters
         * Update the data and trigger a new change
         */
        droppable.onDrop(optionsListener.onChange(() => fetcher.update(true)))

        /**
         * When fetcher loads new data
         * Trigger handler so everything is updated
         */
        droppable.onDrop(fetcher.onChange((page) => listener.trigger(page)))

        /**
         * Deal with unsubbing to fetcher internals
         */
        droppable.onDrop(this.createFetcherUnsubber((handler) => strategy.createAllChangeListener?.(initialOptions, handler), fetcher))

        /**
         * Finally, schedule the start of the fetcher
         */
        this.scheduleFetcherStart(fetcher)

        return Promise.resolve({
            update: (options) => optionsListener.change({ ...optionsListener.value, ...options }),
            onData: (handler) => listener.on(handler),
            stop: () => droppable.drop(),
        })
    }

    subscribeToAll<T extends keyof AllSubscriptionsTypes> (eventName: T, listener: SubscriptionEventHandler<T>): Promise<VoidFunction> {
        return this.rstAdapter.subscribeToResolvedDeviations(eventName, listener)
    }

    subscribeToOne<T extends keyof SingleSubscriptionsTypes> (eventName: T, id: string, listener: SubscriptionEventHandler<T>): Promise<VoidFunction> {
        const droppable = new Droppable()

        const strategy = this.createStrategy(eventName)

        const fetcher = new Fetcher(
            () => strategy.retrieveOne(id),
            (...args) => strategy.compareOne(...args)
        )

        droppable.onDrop(fetcher.onChange(listener))
        droppable.onDrop(this.createFetcherUnsubber((handler) => strategy.createSingleChangeListener?.(id, handler), fetcher))

        this.scheduleFetcherStart(fetcher)

        return Promise.resolve(() => droppable.drop())
    }

    subscribeToCount<T extends keyof CountSubscriptionsTypes> (eventName: T, listener: (count: number | ConnectwareError) => void): Promise<VoidFunction> {
        const droppable = new Droppable()

        const strategy = this.createStrategy(eventName)

        const fetcher = new Fetcher(
            () => strategy.retrieveCount(),
            (a, b) => a === b
        )

        droppable.onDrop(fetcher.onChange(listener))
        droppable.onDrop(this.createFetcherUnsubber((handler) => strategy.createCountChangeListener?.(handler), fetcher))

        this.scheduleFetcherStart(fetcher)

        return Promise.resolve(() => droppable.drop())
    }
}
